import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { BrandMark, cn } from '@mt/ui';
import {
  Flame,
  GraduationCap,
  House,
  LayoutGrid,
  Library,
  LogOut,
  Map as MapIcon,
  Music2,
  Settings,
  Target,
  TrendingUp,
  Trophy,
  Users,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { BRAND } from '../config/brand.ts';
import { useAuth } from '../auth/AuthProvider.tsx';
import { rpc } from '../lib/api.ts';
import { keys, useMe, useStudentHome } from '../lib/queries.ts';
import { OfflineBanner } from '../components/common.tsx';

export type Workspace = 'student' | 'teacher';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  phone?: boolean;
}

const STUDENT_NAV: NavItem[] = [
  { to: '/home', label: 'Home', icon: House, phone: true },
  { to: '/path', label: 'Path', icon: MapIcon, phone: true },
  { to: '/practice', label: 'Practice', icon: Target, phone: true },
  { to: '/classes', label: 'Classes', icon: Users, phone: true },
  { to: '/progress', label: 'Progress', icon: TrendingUp, phone: true },
  // Phones reach it from Home's XP card and the Progress page (the bottom bar holds five items).
  { to: '/leaderboard', label: 'Leaderboard', icon: Trophy },
  { to: '/settings', label: 'Settings', icon: Settings },
];

const TEACHER_NAV: NavItem[] = [
  { to: '/teach', label: 'Classes', icon: LayoutGrid, end: true, phone: true },
  { to: '/teach/templates', label: 'Templates', icon: Library, phone: true },
  { to: '/teach/settings', label: 'Settings', icon: Settings, phone: true },
];

function useWorkspaceSwitch() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return async (target: Workspace) => {
    navigate(target === 'teacher' ? '/teach' : '/home');
    try {
      await rpc('update_profile', { p_patch: { active_workspace: target, ...(target === 'teacher' ? { wants_teacher: true } : { wants_student: true }) } });
      await queryClient.invalidateQueries({ queryKey: keys.me });
    } catch {
      /* the navigation already happened; the preference will be retried next switch */
    }
  };
}

function WorkspaceSwitcher({ workspace, compact }: { workspace: Workspace; compact?: boolean }) {
  const me = useMe();
  const switchTo = useWorkspaceSwitch();
  const canTeach = Boolean(me.data && (me.data.profile.wantsTeacher || me.data.ownsClassrooms));
  const options: { value: Workspace; label: string; icon: LucideIcon }[] = [
    { value: 'student', label: 'Learn', icon: Music2 },
    { value: 'teacher', label: 'Teach', icon: GraduationCap },
  ];
  if (!canTeach && workspace === 'student') return null;
  return (
    <div role="group" aria-label="Workspace" className={cn('grid grid-cols-2 gap-1 rounded-xl bg-surface-2 p-1', compact && 'grid-cols-1')}>
      {options.map((o) => {
        const active = o.value === workspace;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            onClick={() => !active && void switchTo(o.value)}
            className={cn(
              'flex min-h-10 items-center justify-center gap-1.5 rounded-lg px-2 text-sm font-semibold transition-colors',
              active ? 'bg-surface text-ink shadow-card' : 'text-muted hover:text-ink',
            )}
          >
            <o.icon className="size-4" aria-hidden="true" />
            <span className={cn(compact && 'sr-only')}>{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function StudentChips() {
  const home = useStudentHome();
  if (!home.data) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span
        className={cn('inline-flex min-h-9 items-center gap-1 rounded-full px-2.5 text-sm font-bold tabular', home.data.practicedToday ? 'bg-brass-soft text-brass-ink' : 'bg-surface-2 text-muted')}
        title={`${home.data.currentStreak}-day streak`}
      >
        <Flame className={cn('size-4', home.data.practicedToday ? 'text-brass-strong' : '')} aria-hidden="true" />
        <span aria-hidden="true">{home.data.currentStreak}</span>
        <span className="sr-only">{`${home.data.currentStreak} day streak`}</span>
      </span>
      <span className="inline-flex min-h-9 items-center gap-1 rounded-full bg-primary-soft px-2.5 text-sm font-bold text-primary-ink tabular" title="Total XP">
        <Zap className="size-4" aria-hidden="true" />
        <span aria-hidden="true">{home.data.totalXp}</span>
        <span className="sr-only">{`${home.data.totalXp} XP`}</span>
      </span>
    </div>
  );
}

export function AppShell({ workspace }: { workspace: Workspace }) {
  const { signOut } = useAuth();
  const me = useMe();
  const location = useLocation();
  const items = workspace === 'student' ? STUDENT_NAV : TEACHER_NAV;
  const settingsPath = workspace === 'student' ? '/settings' : '/teach/settings';

  return (
    <div className="min-h-dvh bg-paper md:flex">
      <a href="#main" className="sr-only z-50 rounded-lg bg-surface px-4 py-2 font-semibold focus:not-sr-only focus:fixed focus:left-3 focus:top-3">
        Skip to content
      </a>

      {/* Desktop sidebar (≥1024px) and tablet rail (768–1023px). */}
      <aside className="sticky top-0 hidden h-dvh shrink-0 flex-col border-r border-line bg-surface md:flex md:w-22 lg:w-64">
        <div className="flex items-center gap-2.5 px-5 pt-6 pb-5 md:justify-center lg:justify-start">
          <BrandMark />
          <span className="hidden font-display text-lg font-bold lg:inline">{BRAND.name}</span>
        </div>
        <div className="px-3 pb-3 lg:px-4">
          <div className="hidden lg:block">
            <WorkspaceSwitcher workspace={workspace} />
          </div>
          <div className="lg:hidden">
            <WorkspaceSwitcher workspace={workspace} compact />
          </div>
        </div>
        <nav aria-label="Main" className="flex-1 overflow-y-auto px-3 lg:px-4">
          <ul className="flex flex-col gap-1">
            {items.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    cn(
                      'flex min-h-12 items-center gap-3 rounded-xl px-3 font-semibold transition-colors md:flex-col md:justify-center md:gap-1 md:px-1 md:py-2 md:text-xs lg:flex-row lg:justify-start lg:gap-3 lg:px-3 lg:text-[0.95rem]',
                      isActive ? 'bg-primary-soft text-primary-ink' : 'text-ink-2 hover:bg-surface-2',
                    )
                  }
                >
                  <item.icon className="size-5 shrink-0" aria-hidden="true" />
                  <span>{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <div className="border-t border-line p-3 lg:p-4">
          <p className="mb-2 hidden truncate px-1 text-sm text-muted lg:block">{me.data?.profile.displayName}</p>
          <button
            type="button"
            onClick={() => void signOut()}
            className="flex min-h-11 w-full items-center gap-3 rounded-xl px-3 text-sm font-semibold text-ink-2 hover:bg-surface-2 md:justify-center lg:justify-start"
          >
            <LogOut className="size-5" aria-hidden="true" />
            <span className="md:sr-only lg:not-sr-only">Sign out</span>
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Phone / tablet top bar */}
        <header className="sticky top-0 z-30 border-b border-line bg-paper/90 backdrop-blur safe-top md:bg-paper/80">
          <div className="flex min-h-14 flex-wrap items-center justify-between gap-x-3 gap-y-1 px-4 py-1 sm:px-6 lg:px-8">
            <div className="flex min-w-0 items-center gap-2 md:hidden">
              <BrandMark className="size-7" />
              <span className="truncate font-display font-bold">{BRAND.shortName}</span>
            </div>
            <div className="hidden text-sm font-semibold text-muted md:block">{workspace === 'teacher' ? 'Teacher workspace' : 'Your practice'}</div>
            <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
              {workspace === 'student' && <StudentChips />}
              <div className="md:hidden">
                <NavLink to={settingsPath} aria-label="Settings" className="grid size-11 place-items-center rounded-full text-ink-2 hover:bg-surface-2">
                  <Settings className="size-5" aria-hidden="true" />
                </NavLink>
              </div>
            </div>
          </div>
        </header>
        <OfflineBanner />
        <main id="main" className="flex-1 pb-nav md:pb-0" key={location.pathname.split('/').slice(0, 3).join('/')}>
          <Outlet />
        </main>
      </div>

      {/* Phone bottom navigation */}
      <nav aria-label="Main" className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 backdrop-blur md:hidden" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <ul className="mx-auto flex max-w-lg items-stretch justify-around">
          {items
            .filter((i) => i.phone)
            .map((item) => (
              <li key={item.to} className="min-w-0 flex-1">
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    cn('flex min-h-16 flex-col items-center justify-center gap-1 px-0.5 text-[0.7rem] font-semibold', isActive ? 'text-primary' : 'text-muted')
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span className={cn('grid h-8 w-full max-w-12 place-items-center rounded-full transition-colors', isActive && 'bg-primary-soft')}>
                        <item.icon className="size-5" aria-hidden="true" />
                      </span>
                      <span className="max-w-full truncate">{item.label}</span>
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          {workspace === 'teacher' && (
            <li className="min-w-0 flex-1">
              <PhoneWorkspaceButton />
            </li>
          )}
        </ul>
      </nav>
    </div>
  );
}

function PhoneWorkspaceButton() {
  const switchTo = useWorkspaceSwitch();
  return (
    <button type="button" onClick={() => void switchTo('student')} className="flex min-h-16 w-full flex-col items-center justify-center gap-1 px-0.5 text-[0.7rem] font-semibold text-muted">
      <span className="grid h-8 w-full max-w-12 place-items-center rounded-full">
        <Music2 className="size-5" aria-hidden="true" />
      </span>
      <span className="max-w-full truncate">Learn</span>
    </button>
  );
}
